import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { MdFacebook, MdEmail } from 'react-icons/md';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { getIntegrationStatus } from '../../utils/oauthHelper';
import type { RootState } from '../../redux/store';
import type { SocialMedium } from './types';

interface PlatformSelectorProps {
  selectedPlatforms: SocialMedium[];
  onToggle: (platform: SocialMedium) => void;
}

const PlatformSelector: React.FC<PlatformSelectorProps> = ({
  selectedPlatforms,
  onToggle
}) => {
  // Get integrations from Redux (already fetched by Playground component)
  const { data: apiIntegrations } = useSelector(
    (state: RootState) => state.integrations
  );

  // All possible platforms with their UI datax`
  const allPlatforms: { id: SocialMedium; name: string; icon: React.ReactNode; color: string }[] = [
    { id: 'facebook', name: 'Facebook', icon: <MdFacebook size={24} />, color: '#1877F2' },
    { id: 'instagram', name: 'Instagram', icon: <FaInstagram size={24} />, color: '#E4405F' },
    { id: 'tiktok', name: 'TikTok', icon: <FaTiktok size={24} />, color: '#000000' },
    { id: 'twitter', name: 'X', icon: <FaXTwitter size={24} />, color: '#000000' },
    { id: 'email', name: 'Email', icon: <MdEmail size={24} />, color: '#EA4335' }
  ];

  // Filter to only show connected platforms (state: "OK") + email always
  const socialMediums = useMemo(() => {
    return allPlatforms.filter((platform) => {
      // Always show email
      if (platform.id === 'email') {
        return true;
      }
      
      // For other platforms, check if they're connected
      const { isActive } = getIntegrationStatus(apiIntegrations, platform.id);
      return isActive;
    });
  }, [apiIntegrations]);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white">Select Platforms</h3>
      <div className="grid grid-cols-2 gap-3">
        {socialMediums.map((medium) => (
          <motion.button
            key={medium.id}
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onToggle(medium.id)}
            className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-all ${
              selectedPlatforms.includes(medium.id)
                ? 'border-[#EE4423] bg-[#EE4423]/10'
                : 'border-gray-600 bg-[#212121] hover:border-gray-500'
            }`}
          >
            <div style={{ color: medium.color }}>{medium.icon}</div>
            <span className="text-white font-medium">{medium.name}</span>
            {selectedPlatforms.includes(medium.id) && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="ml-auto w-5 h-5 rounded-full bg-[#EE4423] flex items-center justify-center"
              >
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </motion.div>
            )}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default PlatformSelector;
